import React from 'react';
import { IDealerDepartments } from 'interfaces/dealerLocator';
import { useStyles } from './DealerDepartments.styles';
import { Grid, useTheme } from '@mui/material';
import { Icon, Icons } from 'components/Global/Icon/Icon';
import { appPalette } from 'theme/App.palette';
import TextRender from 'components/Global/TextRender/TextRender';
import { trackEvent } from 'helpers/Analytics';
import { LinkButton } from 'components/Global/LinkButton/LinkButton';

export function DealerDepartments({ dealer, labels }: IDealerDepartments) {
  const classes = useStyles();
  const theme = useTheme();
  const isHondaThemed = theme.themeName === 'honda';
  const { acura_red, honda_blue } = appPalette.colors;

  const handleDealerPhoneClick = (departmentName: string) => {
    trackEvent({
      label: `dealer details:call ${dealer?.name}:${departmentName}`,
      category: {
        includePageName: false,
        text: 'locate dealer details',
      },
      type: 'link',
      dealer: {
        dealerId: dealer?.dealerNumber,
        dealerZipcode: dealer?.zipCode,
        dealerName: dealer?.name,
        dealerAddress: `${dealer?.address as string}, ${dealer?.city}, ${
          dealer?.state
        } ${dealer?.zipCode}`,
        dealerPhone: dealer?.phone,
      },
    });
  };

  const renderDepartment = (
    hours: any,
    phone: string | undefined,
    name: string
  ): JSX.Element => {
    return (
      <Grid container className={classes.departmentContainer}>
        <Grid item md={12} className={classes.displayDesktop}>
          <p
            className={`${classes.department_title} ${
              isHondaThemed ? 'atm-title-5' : 'acr-secondary-title-5'
            }`}
          >
            {name}
          </p>
        </Grid>

        {phone && (
          <Grid item xs={4} className={classes.hideDesktop}>
            <h3
              className={`${classes.department_title} mobile ${
                isHondaThemed ? 'atm-title-5' : 'acr-secondary-title-5'
              }`}
            >
              {name}
            </h3>
            <LinkButton
              classNames={{ container: classes.telephoneLink }}
              to={`tel:${phone}`}
              isExternal={true}
              onClick={() => {
                handleDealerPhoneClick(name);
              }}
            >
              <Icon
                type={isHondaThemed ? Icons.filledTelephone : Icons.telephone}
                color={isHondaThemed ? honda_blue : acura_red}
              />
            </LinkButton>
          </Grid>
        )}
        <Grid className={classes.hours_container} item xs={4} md={6}>
          {hours.map((hour: { days: string }, index: number) => {
            return (
              <p
                key={index}
                className={`${classes.hour_text} ${
                  isHondaThemed ? 'atm-caption-6' : 'acr-paragraph-5'
                }`}
              >
                <TextRender text={hour.days} />
              </p>
            );
          })}
        </Grid>
        <Grid className={classes.hours_container} item xs={4} md={6}>
          {hours.map((hour: { hours: string }, index: number) => {
            return (
              <p
                key={index}
                className={`${classes.hour_text} ${
                  isHondaThemed ? 'atm-caption-6' : 'acr-paragraph-5'
                }`}
              >
                <TextRender text={hour.hours} />
              </p>
            );
          })}
        </Grid>
        {phone && (
          <Grid
            item
            md={12}
            className={`${classes.displayDesktop} ${classes.phoneDesktop}`}
          >
            <LinkButton
              classNames={{ container: classes.telephoneLink }}
              to={`tel:${phone}`}
              isExternal={true}
              onClick={() => {
                handleDealerPhoneClick(name);
              }}
              ariaLabel={`Go to ${name} telephone: ${phone} `}
            >
              <Icon
                type={isHondaThemed ? Icons.filledTelephone : Icons.telephone}
                color={isHondaThemed ? honda_blue : acura_red}
              />
              <p
                className={`${
                  isHondaThemed ? 'atm-caption-10' : 'acr-paragraph-5'
                }`}
              >
                <TextRender text={phone} />
              </p>
            </LinkButton>
          </Grid>
        )}
      </Grid>
    );
  };
  const servicePhone = dealer?.servicePhone || dealer?.phone || undefined;
  const partsPhone = dealer?.partsPhone || dealer?.phone || undefined;
  const salesPhone = dealer?.servicePhone || dealer?.phone || undefined;
  if (dealer) {
    return (
      <Grid container direction="row" className="dealer-departments">
        {dealer?.serviceHours &&
          renderDepartment(
            dealer.serviceHours,
            servicePhone,
            labels?.serviceHours
          )}
        {dealer?.partsHours &&
          renderDepartment(dealer.partsHours, partsPhone, labels?.partsHours)}
        {dealer?.salesHours &&
          renderDepartment(dealer.salesHours, salesPhone, labels?.salesHours)}
      </Grid>
    );
  } else {
    return <> </>;
  }
}
